import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaExternalLinkAlt, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { mechanicGarageService, getDocumentUrl } from '../../../../services/apiService';
import { CLUSTERS, getClusterById, normalizeGarageTier } from '../../../../constants/garageServiceClusters';

function normalizeClusterIds(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  return raw
    .map(Number)
    .filter((n) => n >= 1 && n <= 10 && !seen.has(n) && (seen.add(n) || true))
    .sort((a, b) => a - b);
}

function resolveVerificationMap(garageDetails = {}) {
  const raw = garageDetails.clusterVerification || {};
  if (!raw || typeof raw !== 'object') return {};
  return raw;
}

function getClusterStatus(clusterVerification, clusterId) {
  const entry = clusterVerification[String(clusterId)];
  if (!entry) return 'self_declared';
  if (typeof entry === 'string') return entry;
  return entry.status || 'self_declared';
}

function getClusterProofUrl(clusterVerification, clusterId) {
  const entry = clusterVerification[String(clusterId)];
  if (!entry || typeof entry !== 'object') return '';
  return entry.proofUrl || '';
}

const STATUS_STYLES = {
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  self_declared: 'bg-amber-50 text-amber-800 border-amber-200',
};

export default function GarageClusterVerificationPanel({
  garageId,
  garageDetails = {},
  onUpdated,
  compact = false,
}) {
  const [savingId, setSavingId] = useState(null);
  const [localVerification, setLocalVerification] = useState(() => resolveVerificationMap(garageDetails));

  useEffect(() => {
    setLocalVerification(resolveVerificationMap(garageDetails));
  }, [garageDetails]);

  const tier = normalizeGarageTier(
    garageDetails.garageTier || garageDetails.sellerCategory || garageDetails.companyType
  );
  const selectedClusters = useMemo(
    () => normalizeClusterIds(garageDetails.selectedClusters),
    [garageDetails.selectedClusters]
  );

  const clusterRows = useMemo(() => {
    return selectedClusters.map((id) => {
      const cluster = getClusterById(id) || CLUSTERS.find((c) => c.id === id);
      return {
        id,
        label: cluster?.label || `Cluster ${id}`,
        status: getClusterStatus(localVerification, id),
        proofUrl: getClusterProofUrl(localVerification, id),
      };
    });
  }, [selectedClusters, localVerification]);

  if (!garageId) return null;

  if (tier === 'custom_workshop' || tier === 'transformer') {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
        This garage tier uses capability/project tags — cluster verification does not apply.
      </div>
    );
  }

  if (!clusterRows.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
        No service clusters selected yet.
      </div>
    );
  }

  const persistVerification = async (nextVerification) => {
    const payload = {
      garageDetails: {
        clusterVerification: nextVerification,
      },
    };
    const updated = await mechanicGarageService.updateGarage(garageId, payload);
    const user = updated?.garage || updated?.user || updated;
    const gd = user?.garageDetails || garageDetails;
    const saved = resolveVerificationMap(gd);
    setLocalVerification(saved);
    onUpdated?.(user);
    return saved;
  };

  const setClusterStatus = async (clusterId, status) => {
    const key = String(clusterId);
    const prev = localVerification[key];
    const proofUrl = typeof prev === 'object' && prev ? prev.proofUrl || '' : '';

    const next = {
      ...localVerification,
      [key]: {
        status,
        proofUrl,
        verifiedAt: status === 'verified' ? new Date().toISOString() : null,
      },
    };

    setSavingId(clusterId);
    try {
      await persistVerification(next);
      toast.success(status === 'verified' ? 'Cluster marked verified' : 'Cluster marked self-declared');
    } catch (err) {
      toast.error(err.message || 'Failed to update cluster verification');
    } finally {
      setSavingId(null);
    }
  };

  const verifyAll = async () => {
    const next = { ...localVerification };
    for (const row of clusterRows) {
      const key = String(row.id);
      const prev = next[key];
      const proofUrl = typeof prev === 'object' && prev ? prev.proofUrl || '' : '';
      next[key] = {
        status: 'verified',
        proofUrl,
        verifiedAt: new Date().toISOString(),
      };
    }
    setSavingId('all');
    try {
      await persistVerification(next);
      toast.success('All clusters marked verified');
    } catch (err) {
      toast.error(err.message || 'Failed to verify all clusters');
    } finally {
      setSavingId(null);
    }
  };

  const verifiedCount = clusterRows.filter((r) => r.status === 'verified').length;

  return (
    <div className={`rounded-xl border border-gray-200 bg-white overflow-hidden ${compact ? '' : 'shadow-sm'}`}>
      <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex items-center gap-2">
          <FaShieldAlt className="text-sky-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Cluster verification</h3>
            <p className="text-xs text-gray-500">
              {verifiedCount} of {clusterRows.length} verified
              {tier ? ` · ${tier.replace('_', ' ')} garage` : ''}
            </p>
          </div>
        </div>
        <button
          type="button"
          disabled={savingId === 'all' || verifiedCount === clusterRows.length}
          onClick={verifyAll}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {savingId === 'all' ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
          Verify all
        </button>
      </div>

      <ul className="divide-y divide-gray-100">
        {clusterRows.map((row) => {
          const busy = savingId === row.id || savingId === 'all';
          const style = STATUS_STYLES[row.status] || STATUS_STYLES.self_declared;
          return (
            <li key={row.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{row.label}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${style}`}>
                    {row.status === 'verified' ? 'Verified' : 'Self-declared'}
                  </span>
                  {row.proofUrl ? (
                    <a
                      href={getDocumentUrl(row.proofUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 hover:underline"
                    >
                      Proof <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {row.status !== 'verified' ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setClusterStatus(row.id, 'verified')}
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    {busy ? 'Saving...' : 'Verify'}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setClusterStatus(row.id, 'self_declared')}
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                  >
                    {busy ? 'Saving...' : 'Revoke'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
